import { useRef, useState } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";
import { guardEdit } from "@/lib/perms";

// Shared Site Map right rail: per-field stats + numbered table grid (click to
// accept/fail) + the per-table acceptance detail. Used by BOTH the schematic
// SiteMapView and the geographic MapboxSiteMap so the manager-signoff workflow
// is identical on either map. Faithful port of the original vSiteMap() panel —
// reuses the exact class names from global.css (CLAUDE.md styling rule).

export function ScopeChip({ scope }: { scope: string }) {
  const C = eng().C;
  const c = scope === "Pile" ? C.steel : scope === "Module" ? C.hiviz : C.dim2;
  return (
    <span className="scopechip" style={{ background: `${c}22`, color: c, border: `1px solid ${c}66` }}>
      {scope}
    </span>
  );
}

/** Per-table acceptance detail (manager signoff). When `onOpenInspection` is
 *  given, also offers a jump into Quality Control for that table. */
export function TablePanel({ f, t, onSet, onOpenInspection }: { f: any; t: number; onSet: (v: string) => void; onOpenInspection?: (fid: string, t: number) => void }) {
  const e = eng();
  const C = e.C;
  const D = e.D;
  const tt = D.tableTypes[e.tableTypeFor(f, t)];
  const manual = e.tableStatus[`${f.id}::${t}`];
  const eff = e.tableEffectiveStatus(f.id, t);
  const st = eff.status;
  const lab: [string, string] =
    st === "pass" ? ["Accepted", C.grass] : st === "fail" ? ["Failed", C.rust] : st === "partial" ? ["In progress", C.amber] : ["Not inspected", C.dim];

  return (
    <div className="tdetail">
      <div className="tdhead">
        <div>
          <div className="lbl">{f.id}</div>
          <div className="tdt serif">Table {t}</div>
        </div>
        <span className="pill" style={{ background: `${lab[1]}22`, color: lab[1] }}>
          {lab[0].toUpperCase()}
        </span>
      </div>
      {eff.source === "derived" && (st === "pass" || st === "fail") ? (
        <div className="autohint">Auto-derived from QC results · awaiting manager signoff</div>
      ) : null}
      <div className="metagrid" style={{ marginTop: 12 }}>
        <div className="mcell">
          <b>Type</b>
          <span>{tt.label}</span>
        </div>
        <div className="mcell">
          <b>Piles</b>
          <span className="mono">{tt.piles}</span>
        </div>
        <div className="mcell">
          <b>C-rail</b>
          <span className="mono">C120×60×2.0</span>
        </div>
        <div className="mcell">
          <b>Tilt</b>
          <span className="mono">{D.project.tilt}</span>
        </div>
      </div>
      {/* Issue #8: on the map, direct pass/fail wrote a manager signoff that
          never reached the Quality Inspection checklist, so the two disagreed.
          Per the client decision we drop the map's pass/fail and keep a single
          entry point — "Open inspection" routes to QC for this exact table,
          where status flows through one lifecycle. The schematic plan (which
          passes no onOpenInspection) keeps the inline Accept/Fail signoff. */}
      {onOpenInspection ? (
        <>
          <div className="field-l" style={{ marginTop: 14 }}>
            Acceptance
          </div>
          <button className="obtn ok" style={{ marginTop: 4, width: "100%" }} onClick={() => onOpenInspection(f.id, t)}>
            Open inspection →
          </button>
          <div className="auditmeta">
            Acceptance is recorded in Quality Control so the map and the inspection
            checklist always agree. Open the inspection to pass / fail this table.
          </div>
        </>
      ) : (
        <>
          <div className="field-l" style={{ marginTop: 14 }}>
            Acceptance
          </div>
          <div className="resultrow">
            <button
              className="rbtn"
              onClick={() => onSet("pass")}
              style={{ background: manual === "pass" ? C.grass : "var(--bg2)", color: manual === "pass" ? C.bg : C.grass, border: `1px solid ${C.grass}` }}
            >
              ✓ Accept
            </button>
            <button
              className="rbtn"
              onClick={() => onSet("fail")}
              style={{ background: manual === "fail" ? C.rust : "var(--bg2)", color: manual === "fail" ? C.bg : C.rust, border: `1px solid ${C.rust}` }}
            >
              ✕ Fail
            </button>
          </div>
          {manual ? (
            <button className="photobtn" onClick={() => onSet("clear")} style={{ marginTop: 9 }}>
              ↺ Clear manager signoff
            </button>
          ) : null}
          <div className="auditmeta">
            {manual
              ? `Manager signoff: ${manual === "pass" ? "Accepted" : "Failed"} · ${manual === "fail" ? "NCR auto-linked" : "signed"} · device BAL-FIELD-03`
              : eff.source === "derived"
                ? "Status auto-derived from inspection results. Click Accept or Fail to record a formal signoff."
                : "Select Accept or Fail to record acceptance for this table."}
          </div>
        </>
      )}
    </div>
  );
}

/** The full right-rail field panel: header, acceptance stats, clickable table
 *  grid, type legend, and the selected-table detail. Reads/writes the engine's
 *  selField/selTable + tableStatus; bumps the store to re-render. */
export function FieldDetailPanel({ onOpenInspection }: { onOpenInspection?: (fid: string, t: number) => void }) {
  useGrid((s) => s.version); // re-render on engine mutations
  const bump = useGrid((s) => s.bump);
  const e = eng();
  const C = e.C;
  const D = e.D;
  const f = D.fields.find((x: any) => x.id === e.state.selField) || D.fields[0];

  const fieldRoll = (fl: any) => {
    let pass = 0;
    let fail = 0;
    for (let t = 1; t <= fl.tables; t++) {
      const s = e.tableEffectiveStatus(fl.id, t).status;
      if (s === "pass") pass++;
      else if (s === "fail") fail++;
    }
    return { pass, fail, done: pass + fail };
  };

  // punch-pin counts per table (for the cell dots), matching the schematic.
  const showPins = e.state.mapShowPins !== false;
  const pinsPerTable: Record<string, number> = {};
  if (showPins) {
    e.punchPinsForMap({ showResolved: false }).forEach((pin: any) => {
      const it = pin.item;
      if (pin.anchor === "field" && it.workScope !== "station") {
        pinsPerTable[`${it.fid}::${it.t}`] = (pinsPerTable[`${it.fid}::${it.t}`] || 0) + 1;
      }
    });
  }

  const selectTable = (t: number) => { e.state.selTable = t; bump(); };
  const setTableStatus = (v: string) => {
    if (!guardEdit()) return;
    const k = `${f.id}::${e.state.selTable}`;
    e.pushHistory();
    if (v === "clear") delete e.tableStatus[k];
    else e.tableStatus[k] = v;
    e.saveToStorage();
    bump();
    e.toast(
      v === "pass" ? `Table ${e.state.selTable} accepted` : v === "fail" ? `Table ${e.state.selTable} marked fail` : `Table ${e.state.selTable} status cleared`,
      v === "pass" ? "success" : v === "fail" ? "warn" : "info",
    );
  };

  // UI brief #3: in PORTRAIT the panel becomes a bottom sheet — collapsed it shows
  // field · progress · acceptance; swipe/tap up reveals the full table grid. The
  // open/collapsed state is JS-driven; the fixed-sheet layout itself is CSS, gated
  // on @media (orientation: portrait) so landscape is completely unaffected.
  const [sheetOpen, setSheetOpen] = useState(false);
  const touchY = useRef<number | null>(null);
  const onTouchStart = (ev: React.TouchEvent) => { touchY.current = ev.touches[0].clientY; };
  const onTouchEnd = (ev: React.TouchEvent) => {
    if (touchY.current == null) return;
    const dy = ev.changedTouches[0].clientY - touchY.current;
    if (dy < -40) setSheetOpen(true);
    else if (dy > 40) setSheetOpen(false);
    touchY.current = null;
  };

  const roll = fieldRoll(f);
  const acceptancePct = f.tables ? Math.round((roll.pass / f.tables) * 100) : 0;
  const cells = [];
  for (let t = 1; t <= f.tables; t++) {
    const ty = e.tableTypeFor(f, t);
    const tt = D.tableTypes[ty];
    const eff = e.tableEffectiveStatus(f.id, t);
    const st = eff.status;
    let bg = "transparent";
    let fg = C.dim;
    let bd = tt.color;
    if (st === "pass") { bg = C.grass; fg = C.bg; bd = C.grass; }
    else if (st === "fail") { bg = C.rust; fg = C.bg; bd = C.rust; }
    else if (st === "partial") { bd = C.amber; }
    const pCount = pinsPerTable[`${f.id}::${t}`] || 0;
    cells.push(
      <div
        key={t}
        className={`tcell${e.state.selTable === t ? " tsel" : ""}${eff.source === "derived" ? " tauto" : ""}`}
        onClick={() => selectTable(t)}
        style={{ background: bg, color: fg, borderColor: bd, cursor: "pointer" }}
      >
        {t}
        {showPins && pCount > 0 ? (
          <span className="tcell-pdot" title={`${pCount} open punch item${pCount === 1 ? "" : "s"}`}>{pCount > 1 ? pCount : ""}</span>
        ) : null}
      </div>,
    );
  }

  return (
    <div className={`fpanel ${sheetOpen ? "sheet-open" : "sheet-collapsed"}`}>
      {/* Bottom-sheet handle (portrait only — hidden in landscape via CSS). Tap or
          swipe up/down to expand/collapse the table grid + inspection controls. */}
      <div
        className="fpanel-handle"
        onClick={() => setSheetOpen((o) => !o)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="fph-grip" />
        <div className="fph-row">
          <span className="fph-field serif">{f.id}</span>
          <span className="fph-stat">{roll.done}/{f.tables}</span>
          <span className="fph-stat" style={{ color: C.grass }}>{acceptancePct}%</span>
          <span className="fph-hint">{sheetOpen ? "Swipe down to collapse ↓" : "Swipe up for table grid ↑"}</span>
        </div>
      </div>
      <div className="fpanel-body">
      <div className="ph serif">{f.id}</div>
      <div className="psub">{f.tables} tables · Sigma65 fixed-tilt</div>
      <div className="fstats">
        <div className="fcell"><b>Accepted</b><span style={{ color: C.grass }}>{roll.pass}</span></div>
        <div className="fcell"><b>Failed</b><span style={{ color: roll.fail > 0 ? C.rust : C.dim }}>{roll.fail}</span></div>
        <div className="fcell"><b>Inspected</b><span>{roll.done}/{f.tables}</span></div>
        <div className="fcell"><b>Acceptance</b><span style={{ color: C.grass }}>{f.tables ? Math.round((roll.pass / f.tables) * 100) : 0}%</span></div>
      </div>
      <div className="tabgrid-h">{onOpenInspection ? "Table grid · click a table, then open its inspection" : "Table grid · click to accept / fail"}</div>
      <div className="tabgrid">{cells}</div>
      <div className="typelegend">
        {Object.values(D.tableTypes).map((t: any) => (
          <span key={t.label}><i style={{ background: "transparent", borderColor: t.color }} />{t.label}</span>
        ))}
      </div>
      {e.state.selTable ? (
        <TablePanel f={f} t={e.state.selTable} onSet={setTableStatus} onOpenInspection={onOpenInspection} />
      ) : (
        <div style={{ marginTop: 14, fontSize: 11, color: C.dim2, lineHeight: 1.6, border: `1px dashed ${C.line2 || C.line}`, borderRadius: 3, padding: 14 }}>
          {onOpenInspection ? "Click a table in the grid, then open its inspection." : "Click a table in the grid to record acceptance."}
          <br />
          <span style={{ color: C.grass }}>Green</span> = fully verified or manually accepted ·{" "}
          <span style={{ color: C.rust }}>Red</span> = failed · <span style={{ color: C.amber }}>Amber outline</span> = in progress · Plain outline = not yet inspected.
        </div>
      )}
      </div>
    </div>
  );
}
